import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/shared/ProductCard';

const SORTS = [
  { label: 'Newest Arrivals', value: 'newest' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
];

export default function AffordableProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const sort = searchParams.get('sort') || 'newest';

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('priceType', 'affordable');
      if (sort) params.set('sort', sort);
      params.set('page', page);
      params.set('limit', 12);

      const res = await api.get(`/products?${params.toString()}`);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error("Failed to fetch affordable products", err);
    }
    setLoading(false);
  }, [sort, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
    setPage(1);
  };

  return (
    <div className="flex flex-col">
      <header className="relative h-[30vh] md:h-[40vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-[9px] md:text-[10px] uppercase tracking-[0.6em] mb-4 opacity-80">Curated for Value</p>
          <h1 className="font-display text-4xl md:text-7xl font-light tracking-tighter">Affordable Collection</h1>
          <p className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] mt-6 opacity-60">
            {total} Handcrafted Pieces
          </p>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-20 py-16">
        <div className="flex flex-col md:flex-row gap-16">
          <aside className="w-full md:w-48 flex-shrink-0 space-y-12">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-black mb-6">Sort By</p>
              <div className="space-y-3">
                {SORTS.map(s => (
                  <button key={s.value} onClick={() => updateFilter('sort', s.value)}
                    className={`block w-full text-left text-[11px] tracking-wide transition-colors ${sort === s.value ? 'text-black font-bold' : 'text-gray-500 hover:text-black'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-100 aspect-[3/4] mb-6" />
                    <div className="h-3 bg-gray-100 w-1/3 mb-3" />
                    <div className="h-4 bg-gray-100 w-3/4" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-40 bg-gray-50">
                <p className="font-display text-2xl text-gray-400 italic">No affordable pieces available yet.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                  {products.map(p => <ProductCard key={p._id} product={p} />)}
                </div>
                {pages > 1 && (
                  <div className="flex justify-center items-center gap-6 mt-24 pt-12 border-t border-gray-100">
                    {[...Array(pages)].map((_, i) => (
                      <button key={i} onClick={() => setPage(i + 1)}
                        className={`text-[11px] font-sans transition-all ${page === i + 1 ? 'text-black font-bold border-b-2 border-black' : 'text-gray-300 hover:text-black'}`}>
                        0{i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
